import type { LocationGroup } from '../model/shared-types';

/**
 * Given the full set of location groups and the currently selected location IDs,
 * computes the optimal API parameters:
 * - All selected → send nothing (no filter needed)
 * - All locations of some groups selected → send groupIds for those groups, locIds for the rest
 * - Partial selection within groups → send individual locIds
 */
export function computeLocationParams(
  locationGroups: LocationGroup[] | undefined,
  selectedLocations: string[]
): { locIds?: string[]; groupIds?: string } {
  if (!locationGroups || locationGroups.length === 0 || selectedLocations.length === 0) {
    return {};
  }

  // Check if all locations are selected
  const totalLocations = locationGroups.reduce((sum, g) => sum + g.locations.length, 0);
  if (selectedLocations.length === totalLocations) {
    return {}; // All selected = no filter
  }

  const selectedSet = new Set(selectedLocations);
  const fullySelectedGroupIds: string[] = [];
  const partialLocIds: string[] = [];

  for (const group of locationGroups) {
    const groupLocIds = group.locations.map((loc) => loc.id);
    const selectedInGroup = groupLocIds.filter((id) => selectedSet.has(id));

    if (selectedInGroup.length === 0) {
      continue; // Nothing selected in this group
    }

    if (selectedInGroup.length === groupLocIds.length) {
      // All locations in this group are selected → use groupId
      fullySelectedGroupIds.push(group.groupId);
    } else {
      // Partial selection → send individual locIds
      partialLocIds.push(...selectedInGroup);
    }
  }

  const result: { locIds?: string[]; groupIds?: string } = {};

  if (partialLocIds.length > 0) {
    result.locIds = partialLocIds;
  }

  if (fullySelectedGroupIds.length > 0) {
    result.groupIds = fullySelectedGroupIds.join(',');
  }

  return result;
}
